import {
    EventSourceMessage,
    FetchEventSourceInit,
} from '@microsoft/fetch-event-source';
import {
    bufferTime,
    filter,
    map,
    Observable,
    share,
    Subscriber,
} from 'rxjs';

import { logger } from '@src/app/logger';
import { MeticulousSseUtils } from '@src/app/utils/meticulous/meticulousUtils';

import {
    areEventsSyncronizable,
    isDataMessage,
    safeFetchEventSource,
    shouldLogError,
    shouldSyncResource,
    SseFatalError,
    SseRetriableError,
    updateTimestampsMap,
} from '../helpers';
import {
    DataEvent,
    LiveEventCountEvent,
    OutOfSyncUpdate,
    OfferUpdateEvent,
    SseUpdate,
    TimestampsMap,
} from '../types';

export abstract class AbstractSseConnection<T> {
    protected timestampsMap: TimestampsMap = {};
    private observable?: Observable<SseUpdate<T>>;

    constructor(
        private readonly path: string,
        private readonly sseEndpoint: string,
        // use for terminating SSE connection
        private controller = new AbortController(),
    ) {}

    public startStreaming(): void {
        this.observable = this.createObservableFromSseConnectionWithSync();
    }

    public stopStreaming(): void {
        this.controller.abort();
    }

    public getObservable(): Observable<SseUpdate<T>> {
        if (!this.observable) {
            throw new Error('Observable is not initialized');
        }
        return this.observable!;
    }

    public createEventSourceObservable<T>(): Observable<T> {
        let reconnectAfterMs = 1000;
        const sseEndpoint = `${this.sseEndpoint}/${this.path}`;
        return new Observable((observer: Subscriber<T>) => {
            const fetchEventSourceInit: FetchEventSourceInit = {
                // close sse connection when tab is not visible
                openWhenHidden: false,
                onopen: (response) => {
                    MeticulousSseUtils.recordMeticulousSseEvent(sseEndpoint, 'onopen', response);

                    const contentType = response.headers.get('content-type');
                    if (!contentType?.startsWith('text/event-stream')) {
                        throw new SseFatalError(`Expected content-type to be text/event-stream, Actual: ${contentType}`);
                    }
                    if (response.ok && response.status === 200) {
                        return Promise.resolve();
                    }
                    // 429 being the Too Many Requests status code
                    if (response.status >= 400 && response.status < 500 && response.status !== 429) {
                        throw new SseFatalError();
                    }
                    throw new SseRetriableError();
                },
                onmessage: (event: EventSourceMessage) => {
                    MeticulousSseUtils.recordMeticulousSseEvent(sseEndpoint, 'onmessage', event);

                    const parsed = JSON.parse(event.data);
                    // Every message has retry field indicating number of Ms
                    // to wait before reconnecting to SSE stream.
                    reconnectAfterMs = event.retry!;
                    observer.next(parsed);
                },
                onerror: (error): number => {
                    MeticulousSseUtils.recordMeticulousSseEvent(sseEndpoint, 'onerror', error);

                    if (shouldLogError(error)) {
                        logger.error(error);
                    }
                    if (error instanceof SseFatalError) {
                        observer.error(error);
                        throw error;
                    } else {
                        return reconnectAfterMs;
                    }
                },
                onclose: () => {
                    MeticulousSseUtils.recordMeticulousSseEvent(sseEndpoint, 'onclose', undefined);

                    setTimeout(() => {
                        safeFetchEventSource(sseEndpoint, fetchEventSourceInit);
                    }, reconnectAfterMs);
                },
                signal: this.controller.signal,
            };
            safeFetchEventSource(sseEndpoint, fetchEventSourceInit);
        });
    }

    private createObservableFromSseConnectionWithSync(): Observable<SseUpdate<T>> {
        return this.createEventSourceObservable<OfferUpdateEvent>().pipe(
            bufferTime(1_000),
            filter((events) => events.length > 0),
            map((events) => {
                if (!areEventsSyncronizable(events)) {
                    return this.processSseEvents(events as LiveEventCountEvent[]);
                } else {
                    if (events.some((event) => shouldSyncResource(event, this.timestampsMap))) {
                        this.timestampsMap = updateTimestampsMap(events, this.timestampsMap);
                        return OutOfSyncUpdate;
                    } else {
                        this.timestampsMap = updateTimestampsMap(events, this.timestampsMap);
                        const filteredEvents = events.filter(isDataMessage);
                        if (filteredEvents.length === 0) {
                            return null;
                        }
                        return this.processSseEvents(filteredEvents);
                    }
                }
            }),
            filter(Boolean),
            share(),
        );
    }

    abstract processSseEvents(events: DataEvent[] | LiveEventCountEvent[]): T;
}
